import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CommonApiService, FacilityOption } from '../../../services/commonApi';
import { MONTH_OPTIONS, getCurrentMonth } from '../../dashboard/config/constants';
import { getCurrentYear, getYearOptions } from '../config/constants';
import { RootState } from '../../../store';
import { canFilterByCity, requiresCitySelection } from '../../../utils/cityFilterPermissions';

export interface DropdownOption {
	value: string;
	label: string;
}

interface UsePLFiltersReturn {
	// State
	selectedMonth: string;
	selectedYear: string;
	selectedCity: string;
	selectedFacility: string;
	selectedWeek: string;
	// Options
	monthOptions: DropdownOption[];
	yearOptions: DropdownOption[];
	weekOptions: DropdownOption[];
	cityOptions: DropdownOption[];
	facilityOptions: DropdownOption[];
	// Raw facility rows (carries city_id) so pages can resolve the selected
	// facility's own city when the user hasn't picked a city filter — see #56.
	facilities: FacilityOption[];
	// Loading states
	loadingCities: boolean;
	loadingFacilities: boolean;
	// Setters
	setSelectedMonth: (value: string) => void;
	setSelectedYear: (value: string) => void;
	setSelectedCity: (value: string) => void;
	setSelectedFacility: (value: string) => void;
	setSelectedWeek: (value: string) => void;
}

export const usePLFilters = (): UsePLFiltersReturn => {
	const user = useSelector((state: RootState) => state.auth.user);
	const userTypeId = user?.userTypeId;

	// Filter states - default to current month/year
	const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth().toString());
	const [selectedYear, setSelectedYear] = useState<string>(getCurrentYear().toString());
	const [selectedCity, setSelectedCity] = useState<string>('');
	// Internal setter — used only by the auto-pick-a-default-facility effect
	// below. Auto-picking never commits a city, so the founder still sees the
	// full cross-city facility list until they act. The wrapped
	// `setSelectedFacility` (below, returned to callers) is what a *manual*
	// pick uses — that one also commits the city, matching #56 follow-up:
	// pick a facility, the City field and the rest of the facility list
	// narrow to match it, same as picking a city narrows the facility list.
	const [selectedFacility, setSelectedFacilityInternal] = useState<string>('');
	const [selectedWeek, setSelectedWeek] = useState<string>('all');

	// Options states - reuse MONTH_OPTIONS from dashboard constants
	const monthOptions: DropdownOption[] = [...MONTH_OPTIONS];
	const yearOptions = getYearOptions();
	const weekOptions: DropdownOption[] = [
		{ value: 'all', label: 'All' },
		{ value: '1', label: 'Week 1' },
		{ value: '2', label: 'Week 2' },
		{ value: '3', label: 'Week 3' },
		{ value: '4', label: 'Week 4' },
	];
	const [cityOptions, setCityOptions] = useState<DropdownOption[]>([]);
	const [facilityOptions, setFacilityOptions] = useState<DropdownOption[]>([]);
	const [facilities, setFacilities] = useState<FacilityOption[]>([]);
	const [loadingCities, setLoadingCities] = useState(false);
	const [loadingFacilities, setLoadingFacilities] = useState(false);

	// Load cities (only for user types with the city-filter capability)
	useEffect(() => {
		if (!canFilterByCity(userTypeId)) return;

		const loadCities = async () => {
			setLoadingCities(true);
			try {
				const response = await CommonApiService.getCities();
				if ((response.status_code === 200 || response.statusCode === 200) && response.data) {
					const cities = response.data as Array<{
						id: number;
						name: string;
					}>;
					const cityList: DropdownOption[] = cities.map(city => ({
						value: city.id.toString(),
						label: city.name,
					}));
					setCityOptions(cityList);
					// Auto-select Bangalore for roles that must always work one city at
					// a time (role 4). Founders (31) start with no city selected — see
					// all facilities across all cities by default (#56) — and can still
					// narrow to one city manually.
					if (requiresCitySelection(userTypeId)) {
						const bangaloreCity = cityList.find(city => city.label.toLowerCase() === 'bangalore');
						if (bangaloreCity) {
							setSelectedCity(bangaloreCity.value);
						} else if (cityList.length > 0) {
							setSelectedCity(cityList[0].value);
						}
					}
				}
			} catch (error) {
				console.error('Failed to load cities:', error);
			} finally {
				setLoadingCities(false);
			}
		};
		loadCities();
	}, [userTypeId]);

	// Load washing facilities based on selected city (for user_type_id = 4) or all facilities
	useEffect(() => {
		const loadFacilities = async () => {
			setLoadingFacilities(true);
			try {
				let response;
				if (canFilterByCity(userTypeId) && selectedCity) {
					// Load facilities for specific city
					response = await CommonApiService.getFacilities(parseInt(selectedCity));
				} else {
					// Load all facilities
					response = await CommonApiService.getFacilities();
				}

				if ((response.status_code === 200 || response.statusCode === 200) && response.data) {
					const facilityRows = response.data as FacilityOption[];
					const facilityList: DropdownOption[] = facilityRows.map(facility => ({
						value: facility.id.toString(),
						label: facility.location,
					}));
					setFacilities(facilityRows);
					setFacilityOptions(facilityList);
					// Keep the current facility selected if it's still in the (possibly
					// newly city-scoped) list; only fall back to the first facility if
					// it isn't. Without this check, narrowing the list after a manual
					// pick could silently swap the user's choice for whichever facility
					// happens to be first in that city.
					setSelectedFacilityInternal(prev => {
						const stillValid = facilityList.some(f => f.value === prev);
						if (stillValid) return prev;
						return facilityList.length > 0 ? facilityList[0].value : '';
					});
				} else {
					console.warn('Unexpected API response format:', response);
				}
			} catch (error) {
				console.error('Failed to load washing facilities:', error);
			} finally {
				setLoadingFacilities(false);
			}
		};
		loadFacilities();
	}, [userTypeId, selectedCity]); // Reload facilities when city changes (for user_type_id = 4)

	// Manual facility pick: also commit the matching city (every facility
	// belongs to exactly one), so City and the facility list stay consistent
	// — picking a Mumbai facility narrows the list to Mumbai from then on,
	// same as picking Mumbai from the City field would. Auto-picked defaults
	// (above) deliberately skip this so the founder still sees every city's
	// facilities until they actually choose one.
	const setSelectedFacility = (facilityId: string) => {
		setSelectedFacilityInternal(facilityId);
		if (canFilterByCity(userTypeId)) {
			const facility = facilities.find(f => f.id.toString() === facilityId);
			if (facility?.city_id != null) {
				setSelectedCity(facility.city_id.toString());
			}
		}
	};

	return {
		selectedMonth,
		selectedYear,
		selectedCity,
		selectedFacility,
		selectedWeek,
		monthOptions,
		yearOptions,
		weekOptions,
		cityOptions,
		facilityOptions,
		facilities,
		loadingCities,
		loadingFacilities,
		setSelectedMonth,
		setSelectedYear,
		setSelectedCity,
		setSelectedFacility,
		setSelectedWeek,
	};
};
